'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

const STORAGE_KEY = 'smarck_user_email';

const validateEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

interface EmailGateProps {
  children: React.ReactNode;
  toolName: string;
  toolDescription: string;
}

export default function EmailGate({ children, toolName, toolDescription }: EmailGateProps) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && validateEmail(stored)) {
      setHasAccess(true);
    } else {
      if (stored) localStorage.removeItem(STORAGE_KEY);
      setHasAccess(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const apiKey = process.env.NEXT_PUBLIC_ZEROBOUNCE_API_KEY;

      if (!apiKey || apiKey === 'your-zerobounce-api-key-here') {
        setError('Email verification is currently unavailable. Please try again later.');
        setSubmitting(false);
        return;
      }

      // Call ZeroBounce directly from the client
      const zbRes = await fetch(
        `https://api.zerobounce.net/v2/validate?api_key=${encodeURIComponent(apiKey)}&email=${encodeURIComponent(email.trim())}&ip_address=`
      );

      if (!zbRes.ok) {
        setError('Email verification failed. Please try again.');
        setSubmitting(false);
        return;
      }

      const zbData = await zbRes.json();

      // ZeroBounce statuses: valid, invalid, catch-all, unknown, spamtrap, abuse, do_not_mail
      const status: string = (zbData?.status ?? '').toLowerCase();
      const subStatus: string = (zbData?.sub_status ?? '').toLowerCase();

      if (status !== 'valid') {
        let msg = 'This email address could not be verified. Please use a real, working email.';
        if (status === 'invalid') {
          msg = 'This email address does not exist. Please check and try again.';
        } else if (status === 'catch-all') {
          msg = 'This email address could not be confirmed as valid. Please use a different email.';
        } else if (status === 'unknown') {
          msg = 'We could not verify this email address. Please try a different one.';
        } else if (status === 'spamtrap' || status === 'abuse' || status === 'do_not_mail') {
          msg = 'This email address is not accepted. Please use a different email.';
        } else if (subStatus === 'mailbox_not_found' || subStatus === 'no_dns_entries') {
          msg = 'This email address does not appear to exist. Please check and try again.';
        }
        setError(msg);
        setSubmitting(false);
        return;
      }

      // Only reaches here if status === 'valid'

      // Save email to Supabase
      const supabase = createClient();

      const { data: existing } = await supabase
        .from('email_captures')
        .select('id')
        .eq('email', email.trim())
        .maybeSingle();

      if (!existing) {
        const { error: dbError } = await supabase
          .from('email_captures')
          .insert({ email: email.trim(), tool_name: toolName });

        if (dbError) {
          console.error('Failed to save email:', dbError.message);
        }
      }
    } catch (err) {
      console.error('Email verification error:', err);
    }

    localStorage.setItem(STORAGE_KEY, email.trim());
    setTimeout(() => {
      setHasAccess(true);
      setSubmitting(false);
    }, 400);
  };

  if (hasAccess === null) return null;
  if (hasAccess) return <>{children}</>;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div
        className="w-full max-w-lg rounded-2xl px-8 py-10 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255,107,107,0.08) 0%, rgba(78,205,196,0.06) 100%)',
          border: '1px solid rgba(255,107,107,0.2)',
        }}
      >
        {/* Decorative glow */}
        <div
          className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,107,107,0.1) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.08) 0%, transparent 70%)' }}
        />

        <div className="relative z-10">
          {/* Icon */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
            style={{ background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.25)' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          {/* Badge */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-xs font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.2)' }}
            >
              Free Access
            </span>
          </div>

          <h2 className="text-2xl font-bold mb-2" style={{ color: '#E8EDF2' }}>
            Unlock Free {toolName}
          </h2>
          <p className="text-sm mb-6 leading-relaxed" style={{ color: '#A8B2C1' }}>
            {toolDescription} Enter your email to get instant free access — no credit card required.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <label
              className="block text-xs font-semibold tracking-widest uppercase mb-2"
              style={{ color: '#A8B2C1' }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="you@example.com"
              className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all mb-1"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: error ? '1.5px solid #FF6B6B' : '1.5px solid rgba(255,255,255,0.1)',
                color: '#E8EDF2',
              }}
              disabled={submitting}
              autoComplete="email"
            />
            {error && (
              <p className="text-xs mt-1 mb-3" style={{ color: '#FF6B6B' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-4 py-3.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
              style={{
                background: submitting ? 'rgba(255,107,107,0.5)' : '#FF6B6B',
                color: '#fff',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Verifying...
                </>
              ) : (
                <>
                  Get Free Access
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-center mt-5" style={{ color: '#5A6478' }}>
            We&apos;ll only use your email to follow up with insights and updates. No spam.
          </p>
        </div>
      </div>
    </div>
  );
}
